import React, { useState } from "react";
import OrdersTable from "../components/orders/OrdersTable";
import OrderFilters from "../components/orders/OrderFilters";
import OrderHeader from "../components/orders/OrderHeader";
import { useOrderStatuses } from "../hooks/useOrders";
import { useLocations } from "../hooks/useLocation";
import { Spin, Alert } from "antd";
import { Icon } from "@iconify/react";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useAuth } from "../hooks/useAuth";

// Define interface for order status
interface OrderStatus {
  id: number;
  name: string;
}

const Orders: React.FC = () => {
  const { data: user } = useAuth();
  const locationId = user?.location?.id;

  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const { data: orderStatuses, isLoading } = useOrderStatuses();
  const { data: locations } = useLocations();

  // Check permissions
  const { hasPermission } = usePermissions();
  const canViewOrders = hasPermission(PERMISSIONS.ACCESS_ORDERS);
  const canViewAllUsersOrders = hasPermission(
    PERMISSIONS.VIEW_ALL_USERS_ORDERS
  );
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  // Initialize filters - only include location_id if user can't see all locations
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {};
    if (!canSeeAllLocations && locationId) {
      initialFilters.location_id = locationId;
    }
    return initialFilters;
  });

  // Updated icon mapping to better represent each status
  const statusIcons: Record<string, string> = {
    Placed: "mdi:cart-check",
    Confirm: "mdi:clipboard-check",
    Preparing: "mdi:chef-hat",
    Ready: "mdi:food-fork-drink",
    Served: "mdi:food-variant",
    Completed: "mdi:check-circle",
  };

  // Status order based on the process flow with index signature
  const statusOrder: Record<string, number> = {
    Placed: 1,
    Confirm: 2,
    Preparing: 3,
    Ready: 4,
    Served: 5,
    Completed: 6,
  };

  // Sort statuses based on the process flow
  const sortedStatuses = orderStatuses
    ? [...orderStatuses].sort((a: OrderStatus, b: OrderStatus) => {
        // Provide fallback values for unknown statuses
        const orderA = statusOrder[a.name] || 999;
        const orderB = statusOrder[b.name] || 999;
        return orderA - orderB;
      })
    : [];

  // Set default active tab to "Placed" (ID 1) if none is selected
  if (!activeTab && sortedStatuses.length > 0 && !isLoading) {
    setActiveTab(sortedStatuses[0].id);
  }

  // Define all filter fields and conditionally filter out location
  const allFilterFields = [
    { key: "reference", label: "Order ID", type: "text" as const },
    // { key: "user.name", label: "Customer Name", type: "text" as const },
    {
      key: "location_id",
      label: "Location",
      type: "select" as const,
      options:
        locations?.map((location) => ({
          value: location.id,
          label: location.name,
        })) || [],
      requiresPermission: true, // Mark this field as requiring special permission
    },
    // { key: "table_number", label: "Table Number", type: "text" as const },
    { key: "waiter", label: "Waiter", type: "text" as const },
    {
      key: "payment_status_id",
      label: "Payment Status",
      type: "select" as const,
      options: [
        { value: 1, label: "Pending" },
        { value: 2, label: "Paid" },
      ],
    },
  ];

  // Filter out location field if user doesn't have permission
  const filterFields = allFilterFields.filter(
    (field) => !field.requiresPermission || canSeeAllLocations
  );

  // Handle applying filters (following your pattern)
  const handleApplyFilters = (newFilters: Record<string, any>) => {
    // Transform date range filter
    const transformedFilters = { ...newFilters };
    if (newFilters.date_range && newFilters.date_range.length === 2) {
      transformedFilters.start_date = newFilters.date_range[0];
      transformedFilters.end_date = newFilters.date_range[1];
      delete transformedFilters.date_range;
    }

    // Ensure location filter is maintained for users who can't see all locations
    if (!canSeeAllLocations && locationId) {
      transformedFilters.location_id = locationId;
    }

    setFilters(transformedFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters: Record<string, any> = {};
    if (!canSeeAllLocations && locationId) {
      clearedFilters.location_id = locationId;
    }
    setFilters(clearedFilters);
  };

  // If user doesn't have permission to access orders, show error message
  if (!canViewOrders) {
    return (
      <div className="p-6">
        <Alert
          message="Access Denied"
          description="You don't have permission to access orders. Please contact your administrator if you need access."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      {/* Order Header Section */}
      <OrderHeader
        title="Orders Management"
        onToggleFilters={() => setShowFilters(!showFilters)}
      />

      {/* Filters Section (Collapsible) - Following your exact pattern */}
      {showFilters && (
        <OrderFilters
          fields={filterFields}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          canSeeAllLocations={canSeeAllLocations}
          userLocationId={locationId}
        />
      )}

      {/* Tab Header */}
      <div className="flex overflow-x-auto pb-2 mb-4 border-b">
        <div className="flex space-x-4 md:space-x-6">
          {isLoading ? (
            <Spin size="large" />
          ) : (
            sortedStatuses.map((status: OrderStatus) => (
              <button
                key={status.id}
                onClick={() => setActiveTab(status.id)}
                className={`flex flex-col items-center px-3 md:px-4 py-2 transition-colors duration-200 whitespace-nowrap ${
                  activeTab === status.id
                    ? "text-primary border-b-2 border-primary font-semibold"
                    : "text-gray-500 hover:text-primary hover:bg-gray-50"
                }`}
              >
                <Icon
                  icon={statusIcons[status.name] || "mdi:help-circle-outline"}
                  className="text-xl md:text-2xl"
                />
                <span className="mt-1 text-xs md:text-sm">{status.name}</span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Tab Content */}
      <OrdersTable
        orderType={activeTab ?? 1}
        filters={filters}
        canViewAllUsersOrders={canViewAllUsersOrders}
      />
    </div>
  );
};

export default Orders;
